class FaqItem extends HTMLElement {
  #details = null;
  #summary = null;
  #body = null;
  #controller = null;
  #animation = null;
  #isClosing = false;
  #isExpanding = false;

  connectedCallback() {
    this.#details = this.querySelector('.faq-item');
    this.#summary = this.querySelector('.faq-item__summary');
    this.#body = this.querySelector('.faq-item__body');

    if (!this.#details || !this.#summary) return;

    this.toggleAttribute('open', this.#details.open);

    this.#controller = new AbortController();

    this.#summary.addEventListener('click', (event) => this.#onClick(event), {
      signal: this.#controller.signal,
    });

    this.#body?.addEventListener('click', (event) => this.#onBodyClick(event), {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#animation?.cancel();
    this.#controller?.abort();
    this.#controller = null;
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #onClick(event) {
    event.preventDefault();

    const willOpen = this.#isClosing || !this.#details.open;

    if (willOpen) {
      this.#closeOthers();
    }

    if (this.#prefersReducedMotion()) {
      this.#details.open = willOpen;
      this.toggleAttribute('open', willOpen);
      return;
    }

    if (willOpen) {
      this.#open();
    } else {
      this.#close();
    }
  }

  #closeOthers() {
    const container = this.closest('.faq-group, .faq-tab, .faq__group, .faq, section') || document;
    const items = container.querySelectorAll('faq-item');
    items.forEach((item) => {
      if (item !== this && typeof item.close === 'function') {
        item.close();
      }
    });
  }

  close() {
    if (!this.#details) return;

    if (this.#prefersReducedMotion()) {
      this.#details.open = false;
      this.toggleAttribute('open', false);
      return;
    }

    if ((this.#details.open || this.hasAttribute('open') || this.#isExpanding) && !this.#isClosing) {
      this.#close();
    }
  }

  #onBodyClick(event) {
    if (event.target.closest('a')) return;
    if (!window.getSelection()?.isCollapsed) return;
    if (!this.#details.open || this.#isClosing) return;

    if (this.#prefersReducedMotion()) {
      this.#details.open = false;
      this.toggleAttribute('open', false);
      return;
    }

    this.#close();
  }

  #open() {
    this.#details.style.overflow = 'hidden';

    const startHeight = `${this.#details.offsetHeight}px`;

    this.#details.style.height = startHeight;
    this.#details.open = true;
    this.toggleAttribute('open', true);

    window.requestAnimationFrame(() => {
      if (!this.#controller) return;

      const bodyHeight = this.#body ? this.#body.offsetHeight : 0;
      const endHeight = `${this.#summary.offsetHeight + bodyHeight + this.#frameHeight()}px`;

      this.#runAnimation(startHeight, endHeight, true);
    });
  }

  #close() {
    this.toggleAttribute('open', false);

    this.#details.style.overflow = 'hidden';

    const startHeight = this.#details.offsetHeight;
    this.#details.open = false;
    const endHeight = this.#details.offsetHeight;
    this.#details.open = true;
    this.#details.style.height = `${startHeight}px`;

    this.#runAnimation(`${startHeight}px`, `${endHeight}px`, false);
  }

  #frameHeight() {
    const styles = window.getComputedStyle(this.#details);

    return (
      parseFloat(styles.paddingTop) +
      parseFloat(styles.paddingBottom) +
      parseFloat(styles.borderTopWidth) +
      parseFloat(styles.borderBottomWidth)
    );
  }

  #runAnimation(startHeight, endHeight, willOpen) {
    this.#animation?.cancel();

    this.#isExpanding = willOpen;
    this.#isClosing = !willOpen;

    this.#animation = this.#details.animate(
      { height: [startHeight, endHeight] },
      { duration: 200, easing: 'ease-out' },
    );

    this.#animation.onfinish = () => this.#onAnimationFinish(willOpen);
    this.#animation.oncancel = () => {
      this.#isClosing = false;
      this.#isExpanding = false;
    };
  }

  #onAnimationFinish(willOpen) {
    this.#details.open = willOpen;
    this.toggleAttribute('open', willOpen);
    this.#details.style.height = '';
    this.#details.style.overflow = '';
    this.#animation = null;
    this.#isClosing = false;
    this.#isExpanding = false;
  }
}

if (!customElements.get('faq-item')) {
  customElements.define('faq-item', FaqItem);
}
